import { useContext, useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { UserContext } from "../../UserContext";
import { httpGet } from "../../hooks/http";
import { createFilterOptions } from "@mui/material/Autocomplete";

const isEmailValid = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const CommentReply = ({ onReply, onClose, comment }) => {
  const {
    emailSettings: { address, isAuthenticated },
    event: {
      id: eventId,
      primaryContact: { email: contactEmail },
    },
    access_token,
  } = useContext(UserContext);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [replyTo, setReplyTo] = useState([contactEmail]);
  const filter = createFilterOptions();
  const [replySubject, setReplySubject] = useState(
    `Re: ${comment?.content?.slice(0, 100)}`
  );
  const handleOnReply = async () => {
    setIsSending(true);
    if (onReply) {
      await onReply(comment?.id, replyTo, replySubject, replyContent);
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setIsSending(false);
    setReplySubject("");
    setReplyContent("");
    setReplyTo([contactEmail]);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    httpGet(
      `clientPortal/events/${eventId}/contactEmailList`,
      access_token
    ).then((data) => {
      setAutoCompleteOptions(data?.response || []);
    });
  }, [access_token, eventId]);

  const canSend =
    replyContent?.length &&
    replyTo?.length &&
    replySubject?.length &&
    isAuthenticated;

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      open={true}
      onClose={onClose}
    >
      <DialogTitle>
        <Alert severity={"info"} onClose={handleClose}>
          Reply to Comment
        </Alert>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            size="small"
            placeholder="From"
            label="From"
            disabled
            defaultValue={isAuthenticated ? address : ""}
          ></TextField>
          <Autocomplete
            multiple
            autoHighlight
            options={autoCompleteOptions}
            value={replyTo}
            onChange={(e, nv) => {
              setReplyTo(nv.filter(isEmailValid));
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              if (params.inputValue.length <= 3) {
                return filtered;
              }
              return [...filtered, params.inputValue];
            }}
            renderTags={(value, getTagProps) => {
              if (!Array.isArray(value)) return [];
              return value.map((option: string, index: number) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    variant="outlined"
                    label={option}
                    key={key}
                    {...tagProps}
                  />
                );
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!replyTo?.length}
                size="small"
                placeholder="To"
                label="To"
                onKeyDown={(e) => {
                  const nv = e?.target?.value;
                  if (!nv?.length) {
                    return;
                  }
                  if (e.key === "Tab") {
                    setReplyTo((prev) =>
                      prev.concat([nv]).filter(isEmailValid)
                    );
                  }
                }}
                onBlur={(e) => {
                  const nv = e?.target?.value;
                  if (!nv?.length) {
                    return;
                  }
                  setReplyTo((prev) => prev.concat([nv]).filter(isEmailValid));
                }}
              ></TextField>
            )}
          ></Autocomplete>
          <TextField
            size="small"
            placeholder="Subject"
            onChange={(e) => setReplySubject(e?.target?.value)}
            value={replySubject}
            error={!replySubject?.length}
          ></TextField>
          <Stack sx={{ position: "relative" }}>
            <TextField
              size="small"
              multiline
              rows={5}
              value={replyContent}
              error={!replyContent?.length}
              onChange={(e) => setReplyContent(e?.target?.value)}
              inputProps={{
                maxLength: 2500,
              }}
              helperText={`${replyContent?.length}/2500`}
            ></TextField>
          </Stack>
        </Stack>
        {!isAuthenticated && (
          <Alert severity={"error"}>
            You email is not setup or disconnected. Replying is disabled.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button
          onClick={handleOnReply}
          variant="contained"
          disabled={isSending || !canSend}
        >
          {isSending ? <CircularProgress size={20} /> : "Send Reply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default CommentReply;
